import React, { useMemo, useState } from 'react';
import { NavigationContainer, NavigatorScreenParams } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator, StackHeaderProps } from '@react-navigation/stack';

import EcranAccueil from '../screens/EcranAccueil';
import ListeClients from '../screens/ListeClients';
import ProfilClient from '../screens/ProfilClient';
import NouvelleCommande from '../screens/NouvelleCommande';
import GestionFinanciere from '../screens/GestionFinanciere';
import ListeCommandes from '../screens/ListeCommandes';
import DetailCommande from '../screens/DetailCommande';
import LoginScreen from '../screens/LoginScreen';
import GestionStock from '../screens/GestionStock';
import Parametres from '../screens/Parametres';
import ParkVehicules from '../screens/ParkVehicules';
import AbonnementScreen from '../screens/AbonnementScreen';
import ChangePasswordScreen from '../screens/ChangePasswordScreen';
import GestionUtilisateurs from '../screens/GestionUtilisateurs';
import HistoriqueActions from '../screens/HistoriqueActions';
import { buildNavigationTheme } from '../assets/theme';
import { useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { useAppSelector } from '../redux/hooks';
import { isAdminRole } from '../redux/slices/authSlice';
import { MainTabParamList, visibleNavItems } from './navItems';
import TopNav from './TopNav';
import DetailHeader from './DetailHeader';
import { MobileHeader, MobileMenuContext, MobileMenuSheet, MobileTabBar } from './MobileChrome';

export type { MainTabParamList } from './navItems';

export type RootStackParamList = {
  Login: undefined;
  ChangePassword: undefined;
  Main: NavigatorScreenParams<MainTabParamList> | undefined;
  ProfilClient: { clientId: number };
  NouvelleCommande: { clientId?: number; commandeId?: number; vehiculeId?: number };
  DetailCommande: { commandeId: number };
  Abonnement: {
    vehiculeId?: number;
    clientId?: number;
    mode?: 'VEHICULE' | 'INSTALLATION' | 'RENOUVELLEMENT';
    vehiculeIds?: number[];
    servicesPreselectionnes?: string[];
  };
  GestionStock: undefined;
  Parametres: undefined;
};

const Stack = createStackNavigator<RootStackParamList>();
const Sections = createBottomTabNavigator<MainTabParamList>();

const SECTION_SCREENS: Record<keyof MainTabParamList, React.ComponentType> = {
  EcranAccueil,
  ListeClients,
  ParkVehicules,
  ListeCommandes,
  GestionFinanciere,
  GestionStock,
  GestionUtilisateurs,
  Parametres,
  HistoriqueActions,
};

const renderMobileHeader = () => <MobileHeader />;
const renderDetailHeader = (props: StackHeaderProps) => <DetailHeader {...props} />;

/**
 * Main sections. Desktop: top navigation bar in the header, no tab bar.
 * Mobile: logo header + Airbnb-like bottom tab bar, remaining sections in a bottom sheet.
 */
function SectionsNavigator() {
  const { isDesktop } = useLayout();
  const isAdmin = useAppSelector((s) => isAdminRole(s.auth.role));
  const items = visibleNavItems(isAdmin);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeName, setActiveName] = useState<keyof MainTabParamList>('EcranAccueil');
  const menuContext = useMemo(() => ({ openMenu: () => setMenuOpen(true) }), []);

  return (
    <MobileMenuContext.Provider value={menuContext}>
      <Sections.Navigator
        backBehavior="history"
        screenListeners={({ route }) => ({
          focus: () => setActiveName(route.name),
        })}
        screenOptions={({ route }) => ({
          header: isDesktop ? () => <TopNav activeName={route.name} /> : renderMobileHeader,
          tabBarHideOnKeyboard: true,
        })}
        tabBar={(props) => (isDesktop ? null : <MobileTabBar {...props} />)}
      >
        {items.map((item) => (
          <Sections.Screen
            key={item.name}
            name={item.name}
            component={SECTION_SCREENS[item.name]}
            options={{ title: item.label }}
          />
        ))}
      </Sections.Navigator>
      {!isDesktop ? (
        <MobileMenuSheet visible={menuOpen} onClose={() => setMenuOpen(false)} activeName={activeName} />
      ) : null}
    </MobileMenuContext.Provider>
  );
}

function AuthenticatedStack() {
  const { isDesktop } = useLayout();
  const detailOptions = (title: string) => ({
    headerShown: true,
    title,
    header: renderDetailHeader,
  });

  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        gestureEnabled: !isDesktop,
        animationEnabled: !isDesktop,
      }}
    >
      <Stack.Screen name="Main" component={SectionsNavigator} />
      <Stack.Screen name="ProfilClient" component={ProfilClient} options={detailOptions('Profil client')} />
      <Stack.Screen name="NouvelleCommande" component={NouvelleCommande} options={detailOptions('Nouvelle commande')} />
      <Stack.Screen name="DetailCommande" component={DetailCommande} options={detailOptions('Détail de la commande')} />
      <Stack.Screen name="Abonnement" component={AbonnementScreen} options={detailOptions('Abonnement')} />
      <Stack.Screen name="GestionStock" component={GestionStock} options={detailOptions('Gestion du stock')} />
      <Stack.Screen name="Parametres" component={Parametres} options={detailOptions('Paramètres')} />
    </Stack.Navigator>
  );
}

export default function AppNavigator() {
  const token = useAppSelector((s) => s.auth.token);
  const mustChangePassword = useAppSelector((s) => s.auth.mustChangePassword);
  const { colors } = useAppTheme();
  const navigationTheme = useMemo(() => buildNavigationTheme(colors), [colors]);

  return (
    <NavigationContainer theme={navigationTheme}>
      <Stack.Navigator
        key={!token ? 'login' : mustChangePassword ? 'password' : 'app'}
        screenOptions={{ headerShown: false }}
      >
        {!token ? (
          <Stack.Screen name="Login" component={LoginScreen} />
        ) : mustChangePassword ? (
          <Stack.Screen name="ChangePassword" component={ChangePasswordScreen} />
        ) : (
          <Stack.Screen name="Main" component={AuthenticatedStack} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
}
